import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ChevronRight, X, ChevronLeft, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEtkinlikler } from '../../hooks/useSupabaseData';
import { useTranslation } from 'react-i18next';

const MONTHS_TR = ['OCA','ŞUB','MAR','NİS','MAY','HAZ','TEM','AĞU','EYL','EKİ','KAS','ARA'];
const MONTHS_EN = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const parseEventDate = dateStr => {
  try {
    if (!dateStr) return new Date(0);
    if (dateStr.includes('.')) {
      const [d, m, y] = dateStr.split(' ')[0].split('.');
      return new Date(+y, +m - 1, +d);
    }
    return new Date(dateStr);
  } catch { return new Date(0); }
};

const dateBadge = (dateStr, lang = 'tr') => {
  const d = parseEventDate(dateStr);
  if (d.getTime() === 0) return null;
  const months = lang === 'en' ? MONTHS_EN : MONTHS_TR;
  return { month: months[d.getMonth()], day: d.getDate(), year: d.getFullYear() };
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ events, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const ev = events[idx];

  const prev = useCallback(e => { e.stopPropagation(); setIdx(i => (i - 1 + events.length) % events.length); }, [events.length]);
  const next = useCallback(e => { e.stopPropagation(); setIdx(i => (i + 1) % events.length); }, [events.length]);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setIdx(i => (i - 1 + events.length) % events.length);
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % events.length);
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [events.length, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-5 right-5 z-10 p-2.5 text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
        <X size={20} />
      </button>
      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/40 text-xs bg-white/10 px-3 py-1 rounded-full z-10">
        {idx + 1} / {events.length}
      </div>
      {events.length > 1 && (
        <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
          <ChevronLeft size={22} />
        </button>
      )}
      <motion.div
        key={idx} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.22 }}
        className="flex flex-col lg:flex-row gap-8 max-w-5xl w-full items-center"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-center flex-shrink-0">
          <img src={ev.imageURL} alt={ev.baslik} className="max-h-[78vh] max-w-full object-contain rounded-2xl shadow-2xl" />
        </div>
        <div className="text-white min-w-[200px] max-w-xs">
          {ev.category && (
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase bg-white/15 text-white mb-5">
              {ev.category}
            </span>
          )}
          <h2 className="text-2xl font-bold leading-tight mb-5">{ev.baslik}</h2>
          <div className="space-y-2.5 text-white/60 text-sm">
            <div className="flex items-center gap-2.5"><Calendar size={14} />{ev.tarih}</div>
            {ev.konum && <div className="flex items-center gap-2.5"><MapPin size={14} />{ev.konum}</div>}
          </div>
          {ev.aciklama && <p className="mt-4 text-white/45 text-sm leading-relaxed line-clamp-4">{ev.aciklama}</p>}
        </div>
      </motion.div>
      {events.length > 1 && (
        <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
          <ChevronRight size={22} />
        </button>
      )}
    </motion.div>
  );
};

// ── Kart ─────────────────────────────────────────────────────────────────────
const EventCard = ({ event, index, onOpen, lang }) => {
  const badge = dateBadge(event.tarih, lang);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.07 }}
      onClick={() => onOpen(index)}
      className="group flex-shrink-0 w-64 cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      {/* Fotoğraf + Tarih Rozeti */}
      <div className="relative overflow-hidden">
        <img
          src={event.imageURL}
          alt={event.baslik}
          className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* Tarih rozeti */}
        {badge && (
          <div className="absolute top-3 left-3 rounded-xl shadow-lg px-2.5 py-1.5 text-center min-w-[46px]"
               style={{ background: 'linear-gradient(135deg, #7B0D1E, #b91c1c)' }}>
            <span className="block text-[9px] font-black uppercase tracking-wider text-red-200 leading-none">{badge.month}</span>
            <span className="block text-[22px] font-black text-white leading-tight">{badge.day}</span>
            <span className="block text-[9px] font-bold text-red-200 leading-none mt-0.5">{badge.year}</span>
          </div>
        )}
        {/* Afiş göster ikonu */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg">
            <ArrowUpRight size={18} className="text-gray-800" />
          </div>
        </div>
      </div>

      {/* Bilgi */}
      <div className="p-4">
        {event.category && (
          <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
            {event.category}
          </span>
        )}
        <h3 className="text-sm font-bold text-gray-800 leading-snug mb-3 line-clamp-2 group-hover:text-[#7B0D1E] transition-colors duration-200">
          {event.baslik}
        </h3>
        <div className="space-y-1.5 text-[12px] text-gray-500">
          <div className="flex items-center gap-1.5">
            <Calendar size={11} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{event.tarih}</span>
          </div>
          {event.konum && (
            <div className="flex items-center gap-1.5">
              <MapPin size={11} className="text-gray-400 flex-shrink-0" />
              <span className="truncate">{event.konum}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
const EventsSection = () => {
  const { t, i18n } = useTranslation();
  const { etkinlikler } = useEtkinlikler();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollBy = useCallback(dir => {
    scrollRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  }, []);

  const events = useMemo(() => (
    etkinlikler
      .filter(e => e.baslik && e.tarih && e.resim_url)
      .map(e => ({ ...e, baslik: e.baslik.trim(), imageURL: e.resim_url, parsedDate: parseEventDate(e.tarih) }))
      .sort((a, b) => b.parsedDate - a.parsedDate)
      .slice(0, 10)
  ), [etkinlikler]);

  const openLightbox  = useCallback(idx => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  if (!events.length) return null;

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Başlık satırı */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#7B0D1E] mb-2">{t('home.events.badge')}</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
              {t('home.events.upcomingEvents')}
            </h2>
          </div>
          <div className="flex items-center gap-3 pb-1">
            <Link
              to="/etkinlikler"
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[#7B0D1E] transition-colors duration-200 mr-2"
            >
              {t('home.events.viewAll')}
              <ChevronRight size={15} />
            </Link>
            {/* Scroll butonları */}
            <button
              onClick={() => scrollBy(-1)}
              disabled={!canScrollLeft}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#7B0D1E] hover:bg-[#7B0D1E] hover:text-white text-gray-600"
            >
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              disabled={!canScrollRight}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#7B0D1E] hover:bg-[#7B0D1E] hover:text-white text-gray-600"
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </div>

        {/* Yatay kayan kartlar */}
        <style>{`.events-scroll::-webkit-scrollbar{display:none}`}</style>
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="events-scroll flex gap-5 overflow-x-auto pb-4"
          style={{ scrollbarWidth: 'none' }}
        >
          {events.map((event, i) => (
            <EventCard
              key={event.id || event.baslik + i}
              event={event}
              index={i}
              onOpen={openLightbox}
              lang={i18n.language}
            />
          ))}
        </div>

      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            events={events}
            startIndex={lightboxIndex}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default EventsSection;
